/**
 * useOfflineStatus.ts - Hook for monitoring network connectivity
 *
 * Provides:
 * - Current online/offline status
 * - Network quality indicators
 * - Event-based status updates
 * - Data freshness information
 */

import { useState, useEffect, useCallback, useRef } from 'react';
import { offlineStorage, formatDataAge, DataKey } from '../services/OfflineStorage';

export interface OfflineStatusState {
  /** Whether the browser reports being online */
  isOnline: boolean;
  /** Whether we've verified connectivity with the server */
  isServerReachable: boolean;
  /** Combined status - true only if online AND server reachable */
  isConnected: boolean;
  /** When we last successfully synced with server */
  lastSyncTime: number;
  /** Formatted string of data age */
  dataAgeFormatted: string;
  /** Number of pending actions waiting to sync */
  pendingActionsCount: number;
  /** Whether we're currently checking connectivity */
  isChecking: boolean;
}

export interface UseOfflineStatusOptions {
  /** Interval to check server connectivity (ms, default 30000) */
  checkInterval?: number;
  /** URL to ping for connectivity check (default /api/health) */
  healthCheckUrl?: string;
  /** Timeout for health check requests (ms, default 5000) */
  healthCheckTimeout?: number;
  /** Whether to enable automatic health checks */
  enableHealthChecks?: boolean;
}

const DEFAULT_OPTIONS: Required<UseOfflineStatusOptions> = {
  checkInterval: 30000,
  healthCheckUrl: '/health',
  healthCheckTimeout: 5000,
  enableHealthChecks: true,
};

export function useOfflineStatus(options: UseOfflineStatusOptions = {}): OfflineStatusState & {
  checkConnectivity: () => Promise<boolean>;
  refreshDataAge: () => Promise<void>;
} {
  const opts = { ...DEFAULT_OPTIONS, ...options };

  const [state, setState] = useState<OfflineStatusState>({
    isOnline: typeof navigator !== 'undefined' ? navigator.onLine : true,
    isServerReachable: true, // Assume reachable until proven otherwise
    isConnected: typeof navigator !== 'undefined' ? navigator.onLine : true,
    lastSyncTime: 0,
    dataAgeFormatted: 'Unknown',
    pendingActionsCount: 0,
    isChecking: false,
  });

  const checkIntervalRef = useRef<NodeJS.Timeout | null>(null);
  const mountedRef = useRef(true);

  // Check server connectivity
  const checkConnectivity = useCallback(async (): Promise<boolean> => {
    if (!navigator.onLine) {
      if (mountedRef.current) {
        setState(prev => ({
          ...prev,
          isOnline: false,
          isServerReachable: false,
          isConnected: false,
        }));
      }
      return false;
    }

    if (mountedRef.current) {
      setState(prev => ({ ...prev, isChecking: true }));
    }

    try {
      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), opts.healthCheckTimeout);

      const response = await fetch(opts.healthCheckUrl, {
        method: 'HEAD',
        cache: 'no-cache',
        signal: controller.signal,
      });

      clearTimeout(timeoutId);

      const isReachable = response.ok;
      if (mountedRef.current) {
        setState(prev => ({
          ...prev,
          isOnline: true,
          isServerReachable: isReachable,
          isConnected: isReachable,
          isChecking: false,
        }));
      }
      return isReachable;
    } catch (error) {
      if (mountedRef.current) {
        setState(prev => ({
          ...prev,
          isOnline: navigator.onLine,
          isServerReachable: false,
          isConnected: false,
          isChecking: false,
        }));
      }
      return false;
    }
  }, [opts.healthCheckUrl, opts.healthCheckTimeout]);

  // Refresh data age information
  const refreshDataAge = useCallback(async () => {
    try {
      const [lastSyncTime, pendingCount] = await Promise.all([
        offlineStorage.getLastSyncTime(),
        offlineStorage.getPendingActionsCount(),
      ]);

      if (mountedRef.current) {
        setState(prev => ({
          ...prev,
          lastSyncTime,
          dataAgeFormatted: lastSyncTime > 0 ? formatDataAge(lastSyncTime) : 'Never synced',
          pendingActionsCount: pendingCount,
        }));
      }
    } catch (error) {
      console.error('Failed to refresh data age:', error);
    }
  }, []);

  // Handle online/offline events
  useEffect(() => {
    const handleOnline = () => {
      setState(prev => ({ ...prev, isOnline: true }));
      // Verify server connectivity when coming back online
      checkConnectivity();
    };

    const handleOffline = () => {
      setState(prev => ({
        ...prev,
        isOnline: false,
        isServerReachable: false,
        isConnected: false,
      }));
    };

    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);

    return () => {
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
    };
  }, [checkConnectivity]);

  // Setup periodic connectivity checks
  useEffect(() => {
    if (!opts.enableHealthChecks) return;

    // Initial check
    checkConnectivity();
    refreshDataAge();

    // Periodic checks
    checkIntervalRef.current = setInterval(() => {
      checkConnectivity();
      refreshDataAge();
    }, opts.checkInterval);

    return () => {
      if (checkIntervalRef.current) {
        clearInterval(checkIntervalRef.current);
      }
    };
  }, [checkConnectivity, refreshDataAge, opts.checkInterval, opts.enableHealthChecks]);

  // Cleanup on unmount
  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
    };
  }, []);

  return {
    ...state,
    checkConnectivity,
    refreshDataAge,
  };
}

/**
 * Hook to get freshness information for a specific data key
 */
export function useDataFreshness(key: DataKey) {
  const [freshness, setFreshness] = useState<{
    timestamp: number;
    age: number;
    formatted: string;
  } | null>(null);

  const refresh = useCallback(async () => {
    try {
      const result = await offlineStorage.getDataFreshness(key);
      if (result) {
        setFreshness({
          timestamp: result.timestamp,
          age: result.age,
          formatted: formatDataAge(result.timestamp),
        });
      } else {
        setFreshness(null);
      }
    } catch (error) {
      console.error(`Failed to get freshness for ${key}:`, error);
    }
  }, [key]);

  useEffect(() => {
    refresh();
    // Refresh every minute
    const interval = setInterval(refresh, 60000);
    return () => clearInterval(interval);
  }, [refresh]);

  return { freshness, refresh };
}

export default useOfflineStatus;
